import { keyframes, styled } from "@mui/material";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import type { MRT_PaginationState as PaginationState } from "material-react-table";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import AHBoolean from "_shared/booleans/boolean";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { enqueueSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { Masked, MaskedTeam } from "work/masked-fields";
import ProposalIssues, { ProposalIssue, ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { ReviewerEditLockIcon } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import SwitchRedline from "work/proposals/redlining/field-redlines/_views/switch-redline";
import TeamRedlineSelection from "work/proposals/redlining/field-redlines/_views/team-redline-selection";
import FieldRedline, { FieldRedlineArray } from "work/proposals/redlining/field-redlines/field-redline";
import ProposalRedline from "work/proposals/redlining/proposal-redline";
import { TeamTemplateOrderField } from "work/team-templates/_api/queries/team-template-order-field";
import TeamTemplateQuery, {
  TeamTemplateFilterParameters,
  TeamTemplateOrderParameters,
} from "work/team-templates/_api/queries/team-template-query";
import { useGetTeamTemplatesQuery } from "work/team-templates/_api/team-template-api";
import {
  getIsLoadingTeamTemplatesByQuery,
  getTeamTemplatesByQuery,
} from "work/team-templates/_store/team-templates-redux-slice";
import TeamTemplate from "work/team-templates/team-template";
import { TemplateOwnershipScope } from "_shared/_views/templates/template-ownership-filter";
import TemplateSelector, { type TemplateSelectorRow } from "_shared/_views/templates/template-selector";
import ResizableTemplatePanel from "work/proposals/_views/_shared/resizable-template-panel";
import {
  NO_TEMPLATE_CONTRIBUTIONS,
  TemplateContributions,
  withTemplateDeselected,
  withTemplateSelected,
} from "work/proposals/_views/_shared/template-contributions";
import {
  getTeamLeaderCannotAlsoBeMemberMessage,
  getTeamMemberBecameLeaderMessage,
} from "work/proposals/_views/_shared/team-leader-exclusivity-messages";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import {
  paneIsCompact,
  workPaneNarrowerThan,
  workPaneProseInset,
} from "work/_views/work-pane-layout";
import { useWorkPaneIsCompact } from "work/_views/use-work-pane-size";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ListContainer = styled(Grid)(({ theme }) => ({
  height: "100%",
  minHeight: "20rem",
  minWidth: 0,
  width: "100%",
  [paneIsCompact(theme)]: {
    flexDirection: "column",
    minHeight: 0,
  },
}));
// Fills the drag-resizable panel so the template rows follow the column the user sized, rather than
// keeping their own content width and overflowing it.
// The height goes with the width. A Grid item is sized by its contents, and the panel inside ends in
// a paginator MRT drops to the foot of its paper with a `margin-top: auto` — which moves nothing
// unless the paper has height to spare. With this wrapper between the column and the panel the
// paginator sat under the last template rather than at the bottom of the column, where the Fee
// Schedule tab's is; that tab mounts the panel with no wrapper at all.
const TemplateContainer = styled(Grid)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  width: "100%",
  // The fill is for the column case only. On a compact pane this panel is a row in a stack rather
  // than a column beside the roster, and the stack's container has a definite height of its own — so
  // `height: 100%` handed the collapsed accordion the whole tab and left the roster under it with
  // nowhere to draw. See `CollapsibleTemplatePanel`'s `Panel`, which is harmless there for the
  // opposite reason: its parent has no definite height, so the percentage resolves to `auto`.
  [paneIsCompact(theme)]: {
    height: "auto",
  },
}));
// A person revealed under a team template: what they are to it, then what they are called. The list
// this panel replaced said so with "Leader" and "Members" subheaders, which a table of named rows
// has nowhere to put — so the role travels with the person instead of heading a group of them.
const PersonNameCell = styled("div")(({ theme }) => ({
  alignItems: "baseline",
  columnGap: theme.spacing(1),
  display: "flex",
  minWidth: 0,
  width: "100%",
}));
const PersonRole = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  flexShrink: 0,
  fontSize: "0.7rem",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
}));
const PersonName = styled("span")(() => ({
  fontSize: "1em",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const RedlineTeamSelect = styled(TeamRedlineSelection)(({ theme }) => ({
  [workPaneNarrowerThan(theme.breakpoints.values.lg)]: {
    marginTop: theme.spacing(2),
  },
  // The roster's own heading strip is prose and takes the inset; the table under it stays flush to
  // the resize divider, which is already the boundary between the two columns. `data-roster-header`
  // is the hook `TeamRosterSection` documents for addressing the strip without an Emotion class.
  "& [data-roster-header]": workPaneProseInset(theme),
  [paneIsCompact(theme)]: {
    marginTop: theme.spacing(1),
    width: "100%",
  },
}));
const TeamSelectContainer = styled(Grid)(({ theme }) => ({
  minWidth: 0,
  [paneIsCompact(theme)]: {
    width: "100%",
  },
}));
/** The tab's issue notice and its restricted switch — prose, so inset. `workPaneProseInset`. */
const TopControls = styled("div")(({ theme }) => ({
  ...workPaneProseInset(theme),
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const Change = styled("div", { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>(
  ({ theme, active }) => ({
    animation: active ? `${pulseBorder} 1.5s infinite` : "none",
    border: active ? "1px solid" : 0,
    borderColor: theme.palette.primary.main,
    borderRadius: theme.spacing(0.5),
    padding: active ? theme.spacing(2) : 0,
  }),
);
const IssueContainer = styled("div", { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>(
  ({ theme, active }) => ({
    [paneIsCompact(theme)]: {
      "& .MuiTypography-root": {
        fontSize: "0.9rem",
      },
    },
    animation: active ? `${pulseBorder} 1.5s ease-in-out infinite` : "none",
    padding: active ? theme.spacing(1) : "initial",
    borderRadius: theme.shape.borderRadius,
  }),
);

type TeamTabProps = {
  proposalRedline: ProposalRedline;
  issues?: ProposalIssues;
  activeReviewField: ProposalField | undefined;
  negotiationId?: string;
  viewerScope?: string;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  isDrawerOpen: boolean;
  teamLeader: EntityMember | undefined;
  onProposalRedlineChange: (newRedline: ProposalRedline, traversalFieldOverride?: ProposalField | null) => void;
  onTraverseToNewField?: (fieldInfo: ProposalField | undefined) => void;
  onTeamMemberQuitProposal: (memberId: Guid) => void;
  onSelectionDrawerOpenChange?: (isOpen: boolean) => void;
  /** See `TeamRedlineSelectionProps.onTeamLeaderReverted` for why the leader needs its own setter path. */
  onTeamLeaderReverted?: (leader: EntityMember) => void;
};

const TeamTab = (props: Readonly<TeamTabProps>) => {
  const INTERFACE_NAME = "ActiveProposalTeamTab";

  const {
    proposalRedline,
    activeReviewField,
    negotiationId,
    viewerScope,
    disableEditing,
    fieldLockTooltip,
    isDrawerOpen,
    teamLeader,
    onProposalRedlineChange,
    onTraverseToNewField,
    onTeamMemberQuitProposal,
    onSelectionDrawerOpenChange,
    onTeamLeaderReverted,
  } = props;

  const session = useSession();
  const userId = session.user!.id;
  const [templateOwnershipScope, setTemplateOwnershipScope] = useState<TemplateOwnershipScope>("all");
  // The panel's own search box. The team-template API takes no name filter, so this narrows the page
  // in hand rather than the query — see `visibleTeamTemplates`.
  const [templateSearch, setTemplateSearch] = useState("");
  const [templatePagination, setTemplatePagination] = useState<PaginationState>({
    pageIndex: PaginationParameters.DEFAULT_PAGE_INDEX,
    pageSize: PaginationParameters.DEFAULT_PAGE_SIZE,
  });

  // The members redline, or nothing where the viewer may not see it. Every row this tab builds reads
  // it through this rather than re-asking whether the team is masked in each nested builder.
  const unmaskedVendorTeam = MaskedTeam.isPresentNotMasked(proposalRedline.vendorTeam)
    ? proposalRedline.vendorTeam
    : undefined;
  const selectedTemplateIds = unmaskedVendorTeam?.templateIds ?? [];
  const templateQuery = useMemo(() => {
    const filterParameters = new TeamTemplateFilterParameters({
      creatorUserId: templateOwnershipScope === "own" ? userId : undefined,
    });
    const orderParameters = new TeamTemplateOrderParameters({
      orderBy: TeamTemplateOrderField.Name,
      orderDir: OrderDirection.Descending,
    });
    return new TeamTemplateQuery({
      filterParameters,
      orderParameters,
      // The paginator the panel draws has to move the query, or pressing page two would redraw the
      // page the panel already had and call it the second one.
      paginationParameters: PaginationParameters.create(templatePagination),
    });
  }, [templateOwnershipScope, templatePagination, userId]);
  const teamTemplatesApiQuery = useGetTeamTemplatesQuery({ query: templateQuery, interfaceName: INTERFACE_NAME });
  const teamTemplates = useSelector(getTeamTemplatesByQuery(templateQuery));
  const isLoadingTeamTemplates =
    useSelector(getIsLoadingTeamTemplatesByQuery(templateQuery)) ||
    teamTemplatesApiQuery.isLoading ||
    teamTemplatesApiQuery.isFetching;

  const individualUserIds: Set<string> = useMemo(() => {
    const userIds: Set<string> = new Set();
    teamTemplates?.data.forEach((template) => {
      template.members.forEach((member) => userIds.add(member.userId.value));
      userIds.add(template.leader.userId.value);
    });
    return userIds;
  }, [teamTemplates]);

  const individualQuery = useGetIndividualsByUserIdsQuery(
    individualUserIds.size ? { userIds: individualUserIds } : skipToken,
  );

  const individualsPool: Individual[] = useMemo(() => {
    return Individual.fromJSONArray(individualQuery.data) ?? [];
  }, [individualQuery.data]);

  const [templateContributions, setTemplateContributions] =
    React.useState<TemplateContributions>(NO_TEMPLATE_CONTRIBUTIONS);

  const issues = React.useMemo(
    () =>
      props.issues?.entries.filter((issue) =>
        [
          ProposalField.Team,
          ProposalField.TeamRestriction,
          ProposalField.TeamLeader(issue?.field?.id),
          ProposalField.TeamMember(issue.field.id ?? Guid.EMPTY),
        ].some((field) => field.isEqualTo(issue.field)),
      ),
    [props.issues?.entries],
  );
  const isMdDownDisplay = useWorkPaneIsCompact();
  // The panel searches the templates IN HAND rather than the query: the team-template API takes an
  // owner and a page and no name, so there is nothing to send. Each name is matched against the
  // typed text case-insensitively, and the panel is handed the survivors as its rows.
  const templateSearchText = templateSearch.trim().toLocaleLowerCase();
  const visibleTeamTemplates = (teamTemplates?.data ?? []).filter(
    (template) => !templateSearchText || template.name.value.toLocaleLowerCase().includes(templateSearchText),
  );

  async function handleTemplateSelectionToggled(template: TeamTemplate): Promise<void> {
    if (Masked.isMasked(proposalRedline.vendorTeam)) {
      throw new Error("You do not have permission to edit the team members.");
    }
    try {
      if (!template.id) {
        console.error(new Error("No template selected"));
        return;
      }
      const selectedTemplateIds = proposalRedline.vendorTeam.templateIds;
      let updatedSelectedTemplateIds = [...selectedTemplateIds];

      let newProposalRedline = proposalRedline;
      let newTeamMembersRedline: FieldRedlineArray<EntityMember> = proposalRedline.vendorTeam;
      const isAlreadySelected = selectedTemplateIds.some((id) => id.isEqualTo(template.id));
      if (isAlreadySelected) {
        updatedSelectedTemplateIds = updatedSelectedTemplateIds.filter((id) => !id.isEqualTo(template.id));
        const { contributions, itemKeysToRemove: memberKeysToRemove } = withTemplateDeselected({
          contributions: templateContributions,
          templateId: template.id,
          alsoSuppliedItemKeys: getTeamMemberKeysOfLoadedTemplates(updatedSelectedTemplateIds),
        });
        // Read the ids off the redline rather than off the template, so a member the template added
        // and the user has since removed by hand is not removed a second time.
        const entryIdsToRemove = newTeamMembersRedline.redlines.flatMap((memberRedline) =>
          memberRedline.currentEntry && memberKeysToRemove.has(getTeamMemberKey(memberRedline.currentEntry))
            ? [memberRedline.currentEntry.id]
            : [],
        );
        entryIdsToRemove.forEach((entryId) => {
          newTeamMembersRedline = newTeamMembersRedline.removeEntryByFieldId(entryId);
        });
        newTeamMembersRedline = newTeamMembersRedline.updateTemplateIds(updatedSelectedTemplateIds);
        setTemplateContributions(contributions);
      } else {
        updatedSelectedTemplateIds = updatedSelectedTemplateIds.filter((id) => !id.isEqualTo(template.id));
        updatedSelectedTemplateIds.push(template.id);
        const addedMemberKeys: string[] = [];
        // Whoever leads the team cannot also sit in the member list (#4491). Such a member is left
        // out — and out of `addedItemKeys`, so deselecting has nothing of theirs to take back — but
        // the omission is announced rather than left to look like a template that did nothing.
        const membersDisplacedByLeader = template.members.filter((member) =>
          Boolean(teamLeader?.isSamePersonAs(member)),
        );
        template.members.forEach((member) => {
          if (teamLeader?.isSamePersonAs(member)) return;
          if (!getIsMemberAlreadySelected(member)) {
            addedMemberKeys.push(getTeamMemberKey(member));
            newTeamMembersRedline = newTeamMembersRedline.addEntry(member);
          }
        });
        if (membersDisplacedByLeader.length) {
          enqueueSnackbar(getTeamLeaderCannotAlsoBeMemberMessage(getIndividualFullName(teamLeader)), {
            variant: "info",
          });
        }
        newTeamMembersRedline = newTeamMembersRedline.updateTemplateIds(updatedSelectedTemplateIds);
        setTemplateContributions(
          withTemplateSelected({
            contributions: templateContributions,
            templateId: template.id,
            suppliedItemKeys: template.members.map(getTeamMemberKey),
            addedItemKeys: addedMemberKeys,
          }),
        );
      }

      newProposalRedline = proposalRedline.updateTeamMembersRedline(newTeamMembersRedline);
      onProposalRedlineChange(newProposalRedline, null);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to apply template. Please try again", {
        variant: "error",
      });
    }
  }

  function handleToggleTeamMemberSelection(member: EntityMember) {
    if (Masked.isMasked(proposalRedline.vendorTeam)) {
      throw new Error("You do not have permission to edit the team members.");
    }

    // The leader is on the team already, just not as a member, and the two roles are exclusive
    // (#4491). Removing the entry would find nothing to remove, so say why instead.
    if (teamLeader?.isSamePersonAs(member)) {
      enqueueSnackbar(getTeamLeaderCannotAlsoBeMemberMessage(getIndividualFullName(member)), { variant: "info" });
      return;
    }

    if (getIsMemberAlreadySelected(member)) {
      onProposalRedlineChange(
        proposalRedline.updateTeamMembersRedline(
          proposalRedline.vendorTeam.removeEntryByFieldId(member?.id ?? undefined),
        ),
        null,
      );
    } else {
      onProposalRedlineChange(
        proposalRedline.updateTeamMembersRedline(proposalRedline.vendorTeam.addEntry(member)),
        null,
      );
    }
  }

  // Mirrors `EntityMember.isEqualTo`, which `getIsMemberAlreadySelected` uses to decide whether a
  // template member is new, so the same person is the unit on both sides of the toggle.
  function getTeamMemberKey(member: EntityMember): string {
    return `${member.legalEntityId?.value ?? ""}|${member.userId?.value ?? ""}`;
  }

  // Supplements the recorded contributions for templates that were already checked when the tab
  // mounted, which have no record of their own. `teamTemplates` is one ownership-filtered page, so
  // this can only ever KEEP a member a still-selected template supplies, never take one away: what
  // it cannot see is covered by the contribution records instead.
  function getTeamMemberKeysOfLoadedTemplates(templateIds: Guid[]): string[] {
    return (teamTemplates?.data ?? [])
      .filter((loadedTemplate) => templateIds.some((id) => id.isEqualTo(loadedTemplate.id)))
      .flatMap((loadedTemplate) => loadedTemplate.members.map(getTeamMemberKey));
  }

  // Names the person a leader/member collision is about. The individuals pool covers everyone the
  // loaded templates reference, which is everyone who can collide through a template; anyone it has
  // not loaded yet falls back to the message's own wording.
  function getIndividualFullName(member?: EntityMember | null): string | undefined {
    if (!member) return undefined;
    return individualsPool.find((individual) => individual.userId.isEqualTo(member.userId))?.fullName;
  }

  /**
   * Reverting the leader can land on someone a still-selected template has since supplied as a
   * member, and the two roles are exclusive (#4491). The member entry comes off first so the revert
   * does not build a team the API would reject.
   */
  function handleTeamLeaderReverted(revertedLeader: EntityMember) {
    if (!Masked.isMasked(proposalRedline.vendorTeam)) {
      const displacedEntry = proposalRedline.vendorTeam.redlines.find(
        (memberRedline) =>
          memberRedline.currentEntry && revertedLeader.isSamePersonAs(memberRedline.currentEntry),
      )?.currentEntry;
      if (displacedEntry) {
        onProposalRedlineChange(
          proposalRedline.updateTeamMembersRedline(
            proposalRedline.vendorTeam.removeEntryByFieldId(displacedEntry.id),
          ),
          null,
        );
        enqueueSnackbar(getTeamMemberBecameLeaderMessage(getIndividualFullName(revertedLeader)), {
          variant: "info",
        });
      }
    }

    onTeamLeaderReverted?.(revertedLeader);
  }

  function getIsMemberAlreadySelected(member: EntityMember): boolean {
    if (Masked.isMasked(proposalRedline.vendorTeam)) return false;
    return (
      Boolean(teamLeader?.isSamePersonAs(member)) ||
      proposalRedline.vendorTeam.redlines.some((individualRedline) =>
        individualRedline.currentEntry ? individualRedline.currentEntry.isEqualTo(member) : false,
      )
    );
  }

  /** The person leading a template, revealed under it. The leader is not this tab's to change. */
  function buildTemplateLeaderRows(template: TeamTemplate, templateKey: string): TemplateSelectorRow[] {
    const leaderIndividual = individualsPool.find((ind) => ind.userId.isEqualTo(template.leader.userId));
    if (!leaderIndividual) {
      return [];
    }
    const leaderName = individualQuery.isLoading ? "Loading..." : leaderIndividual.fullName;
    return [
      {
        id: `${templateKey}-leader`,
        name: leaderName,
        content: <TemplatePersonCell name={leaderName} teamRole="Leader" />,
        selected: Boolean(teamLeader?.userId?.isEqualTo(template?.leader?.userId)),
        // Locked, as this row has always been on an active proposal: the leader changes here by a
        // revert and by nothing else, so the row reports who leads rather than offering to reseat.
        disabled: true,
        disabledReason: "The team leader cannot be changed here",
        onSelectedChanged: () => undefined,
      },
    ];
  }

  /** The template's roster, revealed under it. A person whose individual never arrived is left out. */
  function buildTemplateMemberRows(template: TeamTemplate, templateKey: string): TemplateSelectorRow[] {
    return (template?.members ?? []).flatMap((member, index) => {
      const individual = individualsPool.find((ind) => ind.userId.isEqualTo(member.userId));
      if (!individual) {
        return [];
      }

      // Someone leading the team is on it, just not as a member — so the box reads as ticked, and it
      // is locked rather than left inert, because the exclusivity rule is not the user's to unpick.
      const isTeamLeader = Boolean(teamLeader?.isSamePersonAs(member));
      const memberName = individualQuery.isLoading ? "Loading..." : individual.fullName;

      return [
        {
          id: `${templateKey}-member-${member.userId?.value ?? index}`,
          name: memberName,
          content: <TemplatePersonCell name={memberName} teamRole="Member" />,
          selected:
            isTeamLeader ||
            (unmaskedVendorTeam?.redlines.some((m) => m.currentEntry?.userId?.isEqualTo(member.userId)) ?? false),
          disabled: isTeamLeader,
          disabledReason: isTeamLeader ? "Already the team leader" : undefined,
          onSelectedChanged: () => handleToggleTeamMemberSelection(member),
        },
      ];
    });
  }

  /** One template's row, with the people it would put on the team folded in under its expander. */
  function buildTemplateRow(template: TeamTemplate): TemplateSelectorRow {
    const isSelected = selectedTemplateIds.some((id) => id.isEqualTo(template.id));
    const templateKey = template.id?.value ?? template.name.value;
    return {
      id: templateKey,
      name: template.name.value,
      selected: isSelected,
      indeterminate:
        !isSelected &&
        (template.userIsReferenced(teamLeader?.userId) ||
          (unmaskedVendorTeam?.redlines
            .map((memberRedline) => memberRedline.field.id)
            .some((memberId) => template.userIsReferenced(memberId ?? undefined)) ?? false)),
      children: [
        ...buildTemplateLeaderRows(template, templateKey),
        ...buildTemplateMemberRows(template, templateKey),
      ],
      onSelectedChanged: () => void handleTemplateSelectionToggled(template),
    };
  }

  /**
   * The templates panel, in the shape the Conflicts tab has had all along: an ownership toggle, a
   * search box, and a table of named rows with checkboxes and an expander (#4955).
   *
   * No `tabs` are passed, and none should be added. A team-template panel has ONE source to draw
   * from — the team templates this actor may reach — and a strip with a single tab in it says
   * nothing. The Conflicts panel's second tab lists documents already used on proposals; the domain
   * has no second list of team templates, so a tab here would have to be invented along with a query
   * to fill it.
   *
   * No `sorting` either: the panel holds every row it is showing, so the table sorts them itself.
   */
  function renderTemplatePanel() {
    if (!unmaskedVendorTeam) {
      return maskedProposalFieldNotice(MaskedProposalField.Teams);
    }
    return (
      <TemplateContainer aria-label="Team templates" role="group">
        <TemplateSelector
          collapsedLabel="Team Templates"
          compact={isMdDownDisplay}
          ownership={{
            label: "team templates",
            value: templateOwnershipScope,
            onChange: setTemplateOwnershipScope,
          }}
          pagination={{
            onChange: setTemplatePagination,
            rowCount: teamTemplates?.totalElements ?? 0,
            value: templatePagination,
          }}
          rows={visibleTeamTemplates.map(buildTemplateRow)}
          search={{ onChange: setTemplateSearch, value: templateSearch }}
          selectedCount={selectedTemplateIds.length}
          status={{
            errorMessage: getErrorMessageFromUnknown(teamTemplatesApiQuery.error),
            isError: teamTemplatesApiQuery.isError,
            isFetching: isLoadingTeamTemplates,
            isLoading: teamTemplatesApiQuery.isLoading,
            onRetry: () => void teamTemplatesApiQuery.refetch(),
          }}
          subject="team templates"
        />
      </TemplateContainer>
    );
  }

  function handleTeamRestrictedChanged(
    newRedline: FieldRedline<AHBoolean>,
    traversalFieldOverride?: ProposalField | null,
  ) {
    try {
      onProposalRedlineChange(proposalRedline.updateTeamRestrictedRedline(newRedline), traversalFieldOverride);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Error updating vendor-team restriction", { variant: "error" });
    }
  }

  if (Masked.isMasked(proposalRedline.vendorTeam) || Masked.isMasked(proposalRedline.teamRestricted)) {
    return maskedProposalFieldNotice(MaskedProposalField.Team);
  }

  const vendorTeamRedline = proposalRedline.vendorTeam;
  const teamRestrictedRedline = proposalRedline.teamRestricted;
  const showLockIcon = Boolean(disableEditing && fieldLockTooltip);
  // `resultingEntry`, not `currentEntry`: an undecided restriction resolves to the value the proposal
  // already carries, so reading the raw current entry would tell someone adding eleven colleagues
  // that the team is unrestricted when the proposal in fact restricts it.
  const teamIsRestricted = teamRestrictedRedline?.resultingEntry?.value ?? false;
  const lockIcon = showLockIcon ? <ReviewerEditLockIcon title={fieldLockTooltip} /> : undefined;

  function renderTopControls() {
    return (
      <TopControls>
        {(issues?.length ?? 0) > 0 && (
          <IssueContainer active={issues?.some((issue) => issue.field.isEqualTo(activeReviewField)) ?? false}>
            {issues?.map((issue: ProposalIssue) => (
              <Typography
                key={issue.field?.cacheKey ?? Guid.generate().value}
                color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                variant="body1"
              >
                {issue.description}
              </Typography>
            ))}
          </IssueContainer>
        )}
        <Change active={activeReviewField?.isEqualTo(ProposalField.TeamRestriction) ?? false}>
          <ListItem disableGutters sx={{ py: 0 }}>
            {showLockIcon && (
              <ListItemAvatar sx={{ alignItems: "center", display: "inline-flex" }}>{lockIcon}</ListItemAvatar>
            )}
            <ListItemText
              disableTypography
              primary={
                <SwitchRedline
                  boolRedline={teamRestrictedRedline}
                  disabled={disableEditing}
                  promptTitleText="Restrict team?"
                  promptMessageText="Restrict team members from freely leaving team without triggering a renegotiation?"
                  okButtonText="Restrict Team"
                  unsetLabel="Team Not Restricted"
                  setLabel="Team Restricted"
                  onBoolRedlineChange={(newRedline, traversalFieldOverride) => {
                    try {
                      handleTeamRestrictedChanged(newRedline, traversalFieldOverride);
                    } catch (error) {
                      console.error(error);
                      enqueueSnackbar("Error updating vendor-team restriction", {
                        variant: "error",
                      });
                    }
                  }}
                />
              }
            />
          </ListItem>
        </Change>
      </TopControls>
    );
  }

  return (
    <ListContainer container direction="row">
      {!disableEditing && (
        <>
          {/*
            One panel, drawn once. The tab used to render the templates twice — a desktop copy and a
            local accordion for the narrow pane, with a CSS media query picking between them — so
            every row and checkbox existed in duplicate. The shared panel collapses itself when told
            the pane is compact, so the second copy is gone and the resizable column is all that is
            left to choose.
          */}
          {isMdDownDisplay ? (
            renderTemplatePanel()
          ) : (
            <ResizableTemplatePanel minimized={isDrawerOpen} selectedCount={selectedTemplateIds.length}>
              {renderTemplatePanel()}
            </ResizableTemplatePanel>
          )}
          {!isMdDownDisplay && (
            <Grid>
              <Divider orientation="vertical" />
            </Grid>
          )}
        </>
      )}
      <TeamSelectContainer size="grow">
        <RedlineTeamSelect
          activeReviewField={activeReviewField}
          topControls={renderTopControls()}
          negotiationId={negotiationId}
          viewerScope={viewerScope}
          disableEditing={disableEditing}
          fieldLockTooltip={fieldLockTooltip}
          teamLeader={teamLeader}
          teamMembersRedline={vendorTeamRedline}
          teamRestricted={teamIsRestricted}
          selectionPresentation="drawer"
          onSelectionDrawerOpenChange={onSelectionDrawerOpenChange}
          onTeamMembersRedlineChange={(
            newTeamRedline: FieldRedlineArray<EntityMember>,
            traversalFieldOverride?: ProposalField | null,
          ) => {
            try {
              onProposalRedlineChange(proposalRedline.updateTeamMembersRedline(newTeamRedline), traversalFieldOverride);
            } catch (error) {
              console.error(error);
              enqueueSnackbar("Error updating vendor-team members", {
                variant: "error",
              });
            }
          }}
          onMemberQuitTeam={(memberId: Guid) => {
            try {
              onTeamMemberQuitProposal(memberId);
            } catch (error) {
              console.error(error);
              enqueueSnackbar("Error quitting vendor-team", { variant: "error" });
            }
          }}
          onTeamLeaderReverted={handleTeamLeaderReverted}
          onMemberClicked={(memberId: Guid) => {
            onTraverseToNewField?.(ProposalField.TeamMember(memberId));
          }}
        />
      </TeamSelectContainer>
    </ListContainer>
  );
};

/**
 * A person under a team template: what they are to it, then what they are called.
 *
 * The list this panel replaced grouped them under "Leader" and "Members" subheaders. A table of
 * named rows has nowhere to put a subheader, so the role travels with the person instead.
 */
function TemplatePersonCell(props: Readonly<{ name: string; teamRole: string }>) {
  return (
    <PersonNameCell>
      <PersonRole>{props.teamRole}</PersonRole>
      <PersonName>{props.name}</PersonName>
    </PersonNameCell>
  );
}

export default TeamTab;
